import { userEndpoints } from "store/apis/userAPI/user.api"
import { IUserStatistics } from "store/apis/userAPI/user.types"
import { PROFESSION_TYPES } from "store/apis/professionsAPI/values"
import { TAGS, api } from "../../api.ts"

export const locationAPI = api.enhanceEndpoints({
  addTagTypes: [
    TAGS.LOCATION,
    TAGS.USER,
    TAGS.PROFESSIONS,
    TAGS.ELIXIRS,
    TAGS.ELIXIRS_ACTIVATED,
    TAGS.BALANCE,
    TAGS.MISSIONS
  ]
})

const locationEndpoints = locationAPI.injectEndpoints({
  endpoints: (builder) => ({
    getAllLocations: builder.query<ILocation[], void>({
      query: () => ({
        url: "/location",
        method: "GET"
      }),
      providesTags: (locations) =>
        locations
          ? [
              ...locations.map(({ _id }: ILocation) => ({
                type: TAGS.LOCATION as const,
                id: _id
              })),
              { type: TAGS.LOCATION, id: "LIST" }
            ]
          : [{ type: TAGS.LOCATION, id: "LIST" }]
    }),
    getActiveSeasonIndex: builder.query<{ seasonIndex: number }, void>({
      query: () => ({
        url: "/location/season/get-active",
        method: "GET"
      }),
      providesTags: [{ type: TAGS.LOCATION, id: "ACTIVE_SEASON" }]
    }),
    getLocationById: builder.query<ILocation, string>({
      query: (id: string) => ({
        url: `/location/${id}`,
        method: "GET"
      }),
      providesTags: (location) =>
        location
          ? [
              { type: TAGS.LOCATION, id: location._id },
              { type: TAGS.LOCATION, id: "LIST" }
            ]
          : [{ type: TAGS.LOCATION, id: "LIST" }]
    }),
    getLocationUserProgress: builder.query<any, void>({
      query: () => ({
        url: "/location/user-progress",
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.USER, id: "progress" }],
      extraOptions: { maxRetries: 3 }
    }),
    collectReward: builder.mutation<ILocationResponse, void>({
      query: () => ({
        url: "/location/collect-reward",
        method: "POST"
      }),
      invalidatesTags: () => [
        { type: TAGS.USER, id: "progress" },
        { type: TAGS.PROFESSIONS, id: PROFESSION_TYPES.SCOUT },
        { type: TAGS.BALANCE },
        { type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" }
      ],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          userEndpoints.util.updateQueryData(
            "getUserGameStatistics",
            undefined,
            (draft: IUserStatistics) => {
              draft.totalMiningCyclesFinished += data?.usedAutoRecycles || 1
            }
          )
        )

        void dispatch(
          locationEndpoints.endpoints.getLocationUserProgress.initiate(
            undefined,
            { forceRefetch: true }
          )
        )
      }
    }),
    startDigging: builder.mutation<ILocationResponse, void>({
      query: () => ({
        url: "/location/start-digging",
        method: "POST"
      }),
      invalidatesTags: () => [{ type: TAGS.ELIXIRS }]
    }),
    skipCycle: builder.mutation<ISkipCycleResponse, void>({
      query: () => ({
        url: "/location/skip-digging-cycle",
        method: "PATCH"
      }),
      invalidatesTags: () => [{ type: TAGS.BALANCE }],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled

        dispatch(
          userEndpoints.util.updateQueryData(
            "getUserGameStatistics",
            undefined,
            (draft: IUserStatistics) => {
              draft.lastSkipDigging = new Date()
            }
          )
        )
      }
    })
  })
})

export const {
  useGetAllLocationsQuery,
  useGetLocationByIdQuery,
  useGetLocationUserProgressQuery,
  useCollectRewardMutation,
  useStartDiggingMutation,
  useGetActiveSeasonIndexQuery,
  useSkipCycleMutation,
  endpoints: {
    getAllLocations,
    getLocationById,
    getLocationUserProgress,
    collectReward,
    startDigging,
    skipCycle
  }
} = locationEndpoints
